/** Rendering helpers shared by every view. Same markup as the mockup. */

import { fmtHours } from './hours';

export const esc = (v: unknown): string =>
  String(v ?? '').replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!,
  );

export const html = (strings: TemplateStringsArray, ...values: unknown[]): string =>
  strings.reduce((out, s, i) => out + s + (i < values.length ? String(values[i] ?? '') : ''), '');

export type State = 'OK' | 'DUE_SOON' | 'OVERDUE' | 'NA';

const GLYPH: Record<State, string> = { OK: '✓', DUE_SOON: '!', OVERDUE: '✕', NA: '–' };
const PILL_CLASS: Record<State, string> = {
  OK: 'p-ok',
  DUE_SOON: 'p-caution',
  OVERDUE: 'p-exceed',
  NA: 'p-neutral',
};
export const STRIPE: Record<State, string> = {
  OK: 's-ok',
  DUE_SOON: 's-caution',
  OVERDUE: 's-exceed',
  NA: 's-none',
};

export const pill = (state: State, label: string): string =>
  html`<span class="pill ${PILL_CLASS[state]}"
    ><span class="g" aria-hidden="true">${GLYPH[state]}</span>${esc(label)}</span
  >`;

/** Maps aircraft status onto the same three-state vocabulary. */
export const statusState = (status: string): State =>
  status === 'GROUNDED' ? 'OVERDUE' : status === 'ATTENTION' ? 'DUE_SOON' : 'OK';

export const statusLabel = (status: string): string =>
  ({
    AIRWORTHY: 'Airworthy',
    ATTENTION: 'Attention',
    GROUNDED: 'Grounded',
    STORED: 'Stored',
    ARCHIVED: 'Archived',
  })[status] ?? status;

export const REGIME_LABEL: Record<string, string> = {
  PART_ML: 'Part-ML',
  PART_M_G: 'Part-M Subpart G',
  FAA_91: 'FAA 14 CFR Part 91',
  ANNEX_I: 'Annex I (national)',
};

export const REGIME_BASIS_LABEL: Record<string, string> = {
  ML_IN_SCOPE: 'Within Part-ML scope',
  AOC_LISTED: 'On a licensed air carrier AOC',
  COMPLEX_MOTOR_POWERED: 'Complex motor-powered',
  NON_EASA_ANNEX_I: 'Annex I — national rules',
  THIRD_COUNTRY_REGISTRY: 'Third-country registry',
};

/**
 * Re-exported so the screens go on importing hours from one place.
 *
 * `fmtHours` follows the organisation's convention — decimal or hours and
 * minutes — and is for a reading or a duration. `fmtDecimalHours` never
 * follows it, and is for a limit, an interval, a tolerance, man-hours or a
 * rate. Which is which, and why, is in `hours.ts`.
 */
export { fmtDecimalHours, fmtHours } from './hours';

export const fmtInt = (n: number): string => n.toLocaleString('en-GB');

/** Follows the organisation's date convention. Which, and why, is in `dates.ts`. */
export { fmtDate } from './dates';

/**
 * How much is left, in the unit that will run out first.
 *
 * Every unit the forecast can drive on is named. Anything not HOURS used to
 * fall through to days, so a task or a component driven by cycles read "500 d
 * left" — a number in the right place with the wrong word against it, which is
 * worse than no number at all.
 *
 * A margin in hours is a duration, so it follows the organisation's convention:
 * `12.5 h left` or `12:30 h left`. The sign is carried by the word after it,
 * which is why the absolute value is formatted.
 */
export const fmtMargin = (remaining: number, unit: string): string => {
  const over = remaining < 0;
  const n = Math.abs(remaining);
  const amount =
    unit === 'HOURS'
      ? `${fmtHours(n)} h`
      : unit === 'CYCLES'
        ? `${n} cyc`
        : unit === 'LANDINGS'
          ? `${n} ldg`
          : `${n} d`;
  return `${amount} ${over ? 'over' : 'left'}`;
};

/**
 * The tolerance meter: the ASI arc flattened. Green band, yellow band, the due
 * line, then the red zone past it, with a needle at consumed interval.
 */
export function arcMeter(
  remaining: number,
  interval: number,
  tolerance: number,
  unit: string,
): string {
  const safeInterval = interval > 0 ? interval : Math.max(Math.abs(remaining) * 2, 1);
  const span = safeInterval + (tolerance || safeInterval * 0.12);
  const dueX = (safeInterval / span) * 100;
  const used = Math.min(Math.max(safeInterval - remaining, 0), span);
  const needle = (used / span) * 100;

  return html`<div class="arcwrap">
    <div class="arc" role="img" aria-label="${esc(fmtMargin(remaining, unit))}">
      <i class="z1" style="left:0;width:${(dueX * 0.72).toFixed(1)}%"></i>
      <i class="z2" style="left:${(dueX * 0.72).toFixed(1)}%;width:${(dueX * 0.28).toFixed(1)}%"></i>
      <i class="z3" style="left:${dueX.toFixed(1)}%;right:0"></i>
      <i class="due" style="left:${dueX.toFixed(1)}%"></i>
      <i class="ndl" style="left:calc(${Math.min(needle, 99).toFixed(1)}% - 1px)"></i>
    </div>
    <span class="rem">${esc(fmtMargin(remaining, unit))}</span>
  </div>`;
}

export const tile = (opts: {
  label: string;
  value: string | number;
  sub?: string;
  tone?: 'ok' | 'caution' | 'exceed' | 'acc';
  suffix?: string;
}): string => html`
  <div class="tile t-${opts.tone ?? 'acc'}">
    <span class="lbl">${esc(opts.label)}</span>
    <b>${esc(opts.value)}${opts.suffix ? html`<span style="font-size:16px">${esc(opts.suffix)}</span>` : ''}</b>
    ${opts.sub ? html`<span class="sub">${esc(opts.sub)}</span>` : ''}
  </div>
`;

export function toast(message: string, tone: 'ok' | 'error' = 'ok'): void {
  const el = document.createElement('div');
  el.textContent = message;
  el.setAttribute('role', 'status');
  el.style.cssText = `position:fixed;left:50%;bottom:26px;transform:translateX(-50%);z-index:200;
    background:${tone === 'error' ? 'var(--exceed)' : 'var(--ink)'};color:var(--panel);
    font:600 13px var(--ui);padding:10px 18px;border-radius:22px;max-width:min(520px,92vw);
    box-shadow:0 10px 30px -8px rgba(0,0,0,.5)`;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 3200);
}

/** Skeleton shown while a view's data is in flight. */
export const loading = (label = 'Loading'): string => html`
  <div class="card">
    <div class="card-b" style="display:flex;align-items:center;gap:10px;color:var(--ink-3)">
      <span class="lbl">${esc(label)}…</span>
    </div>
  </div>
`;

export const errorCard = (message: string, retry?: string): string => html`
  <div class="card">
    <div class="card-b">
      <div class="callout c-exceed">
        <span class="cg" aria-hidden="true">✕</span>
        <div>
          ${esc(message)}
          ${retry ? html`<p style="margin-top:8px"><button class="btn sm" data-action="${esc(retry)}">Try again</button></p>` : ''}
        </div>
      </div>
    </div>
  </div>
`;
